package com.swetdot.swetgram.domain

import kotlin.time.Instant

/*
 * Client domain models for the Groups surface.
 *
 * Same rule as `Models.kt` and `Profile.kt`: nothing in this file may import from
 * `com.swetdot.swetgram.protocol` (IMPLEMENTATION_NOTES §10). `protocol/dto/Groups.kt` holds the
 * wire types; `data/groups/GroupRepository.kt` is the only place the two meet.
 *
 * WHAT THIS FILE DELIBERATELY IS NOT: a Kotlin copy of `Group`. Four things are different, and each
 * of them is a decision the mapper makes once instead of every screen making it again:
 *
 *  1. [GroupId] and [ChatId] are separate types. The wire sends `id` and `conversation_id` as two
 *     bare `Uuid` strings, which are interchangeable to the compiler and are NOT interchangeable to
 *     the server -- `/groups/{group_id}` and `/conversations/{conversation_id}/messages` are
 *     different resources and nothing in the contract says the two values match. Typing them apart
 *     turns "passed the wrong id" from a 404 at runtime into a compile error.
 *  2. Every wire enum has an `UNKNOWN` member. None of the enums here do. An unknown role or an
 *     unknown posting policy is not a state a screen can render, so it is resolved at the boundary
 *     -- always toward the least permission -- rather than at each call site. Note the limit of
 *     that: only the literal `unknown` is absorbed, because a value outside the contract fails the
 *     decode by design and never becomes a domain value at all.
 *  3. `username` and `public_url` become one non-null [PublicAddress] or nothing at all, because
 *     the contract guarantees they arrive together and a half of one is not a shareable link.
 *  4. Roles and policies are folded into [GroupPermissions], so a screen asks whether it may show a
 *     control instead of re-deriving SPS §12.7's table from a role and two enums.
 *
 * SOURCES. SPS §12 (Groups) and §7 (Information Architecture) are the product shape: a group is a
 * chat plus a Discover area plus a member list, reached from its own bottom-navigation tab, and
 * §12.7's permission table is what [GroupPermissions] encodes. SDS §14.1 is the surface: the card
 * shows avatar, name, member count and description, and the group screen has three sections.
 *
 * WHERE THE FOLDER AND THE CONTRACT DISAGREE, THE CONTRACT WINS, and it disagrees four times:
 *
 *  * SPS §12.6 lists a Members tab with "member list; admins; search members; member card; leave
 *    group action" and never mentions adding somebody to a group that already exists. The contract
 *    agrees: there is no add-member operation anywhere in v1.0.0. Members arrive through
 *    `initial_member_ids` at creation ([NewGroup.initialMemberIds]) or through the invite link.
 *    So [GroupPermissions] has no `canAddMembers` -- see the note there.
 *  * SPS §12.7 gives Admin "Invite members: Yes" unconditionally and Member "Yes or configurable".
 *    The contract instead derives `invite_permission` from visibility, and DECISIONS §7 states the
 *    rule: public groups all members, private groups Owner and Admin only. The contract's version
 *    is what [GroupPermissions.canInviteMembers] reads, from the value the server sent.
 *  * STA §9.2 says "invite token can be disabled". THE FROZEN CONTRACT HAS NO SUCH OPERATION --
 *    no delete, no revoke, no disable. `createOrRotateInviteLink` is the only writer and it always
 *    leaves a live replacement, so there is no [GroupInviteLink] state meaning "off" and no
 *    permission for one. Recorded again in DECISIONS §7; the contract wins.
 *  * STA §13.5 gives `POST /groups/:id/members/:user_id/promote` and lists no ban or join-request
 *    endpoints at all. The contract has `PATCH .../members/{user_id}/role` with a body, and six
 *    moderation operations. [GroupRoleAssignment] and [GroupJoinDecision] are shaped from the
 *    contract's bodies, not from the folder's paths.
 *
 * WHERE THE FOLDER IS SILENT, and this is called out rather than dressed up as spec, exactly as the
 * pinned-message pass did: neither document says anything about optimistic concurrency, about what
 * a screen should do when a settings save loses a race, or about what happens when an owner tries
 * to leave. [GroupSettingsWrite] and the treatment of `ownership_transfer_required` in
 * [GroupFailure] are therefore DECISIONS made here, taken from what the deployed server actually
 * answers, not requirements read off the folder.
 *
 * The folder is silent about ALL of the invite, join-request and ban surface too, and that silence
 * is much larger than the one above. SPS §12.3 is two sentences -- "Groups support invite links.
 * Invite links allow communities to migrate from other platforms" -- with no screen, no states and
 * no rotation semantics; SPS §12.7's table names "Promote admin" and nothing about bans; SDS §14
 * describes a group card and a group identity block and no moderation surface whatsoever. Every
 * shape below that the contract did not dictate is therefore a DECISION and is labelled as one,
 * both here and in the screens.
 */

// ------------------------------------------------------------------------------------- ids

/**
 * A group.
 *
 * NOT the chat inside it -- that is [Group.conversationId], a [ChatId]. Both are UUIDs on the wire
 * and the contract nowhere says they are equal; the observed server issues two different values.
 */
data class GroupId(val value: String)

/**
 * One row of a private group's join queue.
 *
 * Its own type rather than a bare `String` for the reason [GroupId] and [ChatId] are separate:
 * `resolveGroupJoinRequest` addresses `/groups/{group_id}/join-requests/{request_id}` and both
 * segments are UUIDs, so passing the group id where the request id belongs is a mistake the
 * compiler can catch instead of the server.
 */
data class GroupJoinRequestId(val value: String)

// ----------------------------------------------------------------------------------- enums

/**
 * Public or private. Fixed at creation and never editable: `UpdateGroupRequest` offers name,
 * description, username and avatar and deliberately not this.
 *
 * There is no third member. The wire enum has `unknown`, and it resolves to [PRIVATE] at the
 * boundary -- the failing-closed direction, since presenting a group as publicly discoverable on
 * the strength of a value the client could not read would advertise a member list and a Discover
 * area that may have been meant for nobody.
 */
enum class GroupVisibility { PUBLIC, PRIVATE }

/**
 * A membership role, most senior first. SPS §12.7 fixes the MVP set at exactly these three.
 *
 * The wire enum's `unknown` maps to [MEMBER] here. That is the floor and not a fourth state:
 * every operation that returns a role has already established that the caller is in the group (a
 * non-member gets `404 conversation_not_found`), so "in the group with the fewest permissions" is
 * the honest reading of a role this build cannot name. Because [GroupPermissions] is derived from
 * this enum, the practical effect is that an unreadable role shows no admin control, no destructive
 * action, and no composer in a group whose chat is restricted -- a permission nobody granted is
 * never rendered.
 */
enum class GroupRole { OWNER, ADMIN, MEMBER }

/**
 * Who may post: everyone in the group, or only Owner and Admin.
 *
 * Covers `discover_posting`, `chat_posting` and the read-only `invite_permission`. The wire enum's
 * `unknown` maps to [ADMINS_ONLY], the restrictive member, for the same reason as everything else
 * here. A settings form must not then SAVE that guess back, which is why an edit sends only the
 * fields it touched -- see [GroupSettingsEdit].
 */
enum class GroupPostingPolicy { ALL_MEMBERS, ADMINS_ONLY }

/** Seniority, used by the removal rule. Nothing outside this file needs it. */
private fun rankOf(role: GroupRole): Int = when (role) {
    GroupRole.OWNER -> 3
    GroupRole.ADMIN -> 2
    GroupRole.MEMBER -> 1
}

// --------------------------------------------------------------------------------- address

/**
 * A public group's username and the link built from it.
 *
 * One type rather than two nullable fields, because the two are one fact. The contract sends
 * `username` and `public_url` together for a public group and neither for a private one, and
 * `public_url` is DERIVED from the username rather than stored (DECISIONS §7), so it changes when
 * the username does. A group holding one without the other is not a state the server produces, and
 * making it unrepresentable means `publicAddress != null` is the single question a share sheet has
 * to ask.
 *
 * If a server ever sent a username with no URL, the mapper drops the pair rather than inventing an
 * origin: a share button that copies a link the client made up is worse than no share button.
 */
data class PublicAddress(
    /** Lowercase, its own namespace -- `@alex` the group and `@alex` the person can coexist. */
    val username: String,
    /** e.g. `https://swetgram.com/g/community`. */
    val url: String,
)

// -------------------------------------------------------------------------------- settings

/**
 * A group's posting permissions, and the ONE version number a group has.
 *
 * The version belongs to the settings and to nothing else. `Group` carries no version at all --
 * confirmed against the deployed server, whose `Group` body has no such property -- so renaming a
 * group is not under optimistic concurrency and must not consume this counter. Only
 * [com.swetdot.swetgram.data.groups.GroupRepository.updateSettings] can answer
 * `409 version_conflict`.
 *
 * [invitePermission] is read-only. `GroupSettingsUpdate` cannot set it and the server derives it
 * from visibility, so it is carried here as sent rather than re-derived on the client, where the
 * two could drift with no endpoint able to correct it.
 */
data class GroupSettings(
    val discoverPosting: GroupPostingPolicy,
    val chatPosting: GroupPostingPolicy,
    /** Derived by the server from visibility; there is no operation that changes it. */
    val invitePermission: GroupPostingPolicy,
    /** The optimistic-concurrency token for the next settings write. */
    val version: Int,
) {
    /** A blank edit pinned to the revision these settings were read at. */
    fun startEdit(): GroupSettingsEdit = GroupSettingsEdit(version = version)
}

/**
 * A pending change to [GroupSettings].
 *
 * Both policies default to null, meaning "leave unchanged", and only the ones a form actually
 * touched are sent. That is not just politeness about concurrent edits -- it is what stops the
 * boundary's fail-closed mapping from becoming a write. A `chat_posting` this build could not read
 * arrives as [GroupPostingPolicy.ADMINS_ONLY]; if a save then posted every field back, a policy the
 * user never chose and never saw would be stored as though they had.
 *
 * [version] is required and has no default: it is what the server compares to answer
 * `409 version_conflict`, and a defaulted one would let a stale write through. Build one with
 * [GroupSettings.startEdit] so it is always pinned to a revision that was really read.
 */
data class GroupSettingsEdit(
    val version: Int,
    val discoverPosting: GroupPostingPolicy? = null,
    val chatPosting: GroupPostingPolicy? = null,
) {
    /**
     * The contract's `minProperties: 2` -- version plus at least one policy. An edit that changes
     * nothing is a guaranteed 400, so it never costs a round trip.
     */
    val hasChanges: Boolean get() = discoverPosting != null || chatPosting != null
}

/**
 * What a settings save actually did.
 *
 * A sealed result rather than a thrown error, and this is the one place the shape is unusual on
 * purpose. `409 version_conflict` is not a failure the user can be told about and left with: their
 * form is now describing a revision that no longer exists, and the only way forward is to re-read
 * the settings and let them re-apply the choice. Making that a VALUE means an exhaustive `when`
 * forces a screen to handle it; making it an exception means a screen can forget, and forgetting
 * looks exactly like the save having worked.
 *
 * NEITHER the SPS nor the SDS says anything about this. It is a decision, taken from what the
 * deployed server does: a stale `version` answers `409 version_conflict`, and a `version` HIGHER
 * than the stored one is stale too, because the client is echoing a revision this server never
 * issued.
 */
sealed interface GroupSettingsWrite {
    /** The write landed. [settings] carries the new [GroupSettings.version]. */
    data class Updated(val settings: GroupSettings) : GroupSettingsWrite

    /**
     * Somebody else changed the settings first. Nothing was written.
     *
     * [current] is what the server holds NOW, already re-read, so the form can be rebuilt without
     * a second round trip and without the screen having to know that a conflict implies a refetch.
     */
    data class Stale(val current: GroupSettings) : GroupSettingsWrite
}

// ----------------------------------------------------------------------------- permissions

/**
 * SPS §12.7's permission table, resolved for one viewer in one group.
 *
 * Derived rather than stored, so it cannot fall out of step with the role and settings it comes
 * from. Screens ask these questions instead of comparing roles, which keeps the table in one place
 * -- and keeps it honest, because every field below was checked against what the deployed server
 * actually enforces rather than against the table alone.
 *
 * These are for HIDING controls, never for granting them. The server re-checks all of it; a screen
 * that got one of these wrong in the permissive direction produces a 403 the user cannot act on,
 * which is why each one leans strict.
 *
 * THERE IS NO `canAddMembers`, and its absence is the point. The contract has no operation that
 * adds someone to an existing group -- probed against the live server, `POST /groups/{id}/members`
 * is a 404 -- and SPS §12.6 never asks for one. People arrive through [NewGroup.initialMemberIds]
 * at creation or through an invite link.
 *
 * THE THREE MODERATION PREDICATES BELOW ARE NEW, and two of them are decisions rather than table
 * entries. [canChangeRoles] is spec: SPS §12.7 says "Promote admin -- Owner: Yes, Admin: No,
 * Member: No", and demotion is the same operation with the other body value. [canModerate] and
 * [canRotateInviteLink] are NOT in the table -- SPS §12.7 has no ban row, no join-request row and
 * no rotate row -- so they are derived, and they are derived the same way the server derives them:
 * "could this person remove a plain member?", which is §12.7's removal row. That grounding is
 * deliberate, because the server's `GroupModerationPermission` answers the four untargeted
 * operations (unban, both listings, resolve) with exactly that question rather than a second table.
 */
data class GroupPermissions(
    /** `chat_posting` applied. False hides the composer; the server answers `group_chat_read_only`. */
    val canSendMessages: Boolean,
    /** `discover_posting` applied, for the group's own Discover area (SPS §12.5). */
    val canPostToDiscover: Boolean,
    /**
     * `invite_permission` applied: whether this viewer may SEE and share the group's invite link.
     *
     * Not the same question as [canRotateInviteLink], and the split is the contract's fault rather
     * than a preference. `createOrRotateInviteLink` is one operation for two acts, and DECISIONS §7
     * splits the authority: a public group's plain member receives the CURRENT link (all members
     * may share the invitation), while replacing a link that still works is Owner and Admin only.
     */
    val canInviteMembers: Boolean,
    /** SPS §12.7: Owner yes, Admin yes, Member no. Per-target rules are in [Group.mayRemove]. */
    val canRemoveMembers: Boolean,
    /**
     * Owner only. SPS §12.7's "Promote admin" row, read literally: an Admin may not create another
     * Admin, and demotion is `updateGroupMemberRole` with the other body value.
     *
     * `owner` is not an assignable value -- see [GroupRoleAssignment] -- so this never grants a
     * takeover. Per-target rules are in [Group.mayChangeRole].
     */
    val canChangeRoles: Boolean,
    /**
     * Owner and Admin: the ban list, the join queue, banning, unbanning, and resolving a request.
     *
     * DERIVED from §12.7's removal row rather than declared, because the folder declares none of
     * these operations at all -- STA §13.5 lists no ban and no join-request endpoint, and SPS §12.7
     * has no row for either. Stated as a decision in the screens that read it.
     */
    val canModerate: Boolean,
    /**
     * Owner and Admin, per STA §9.2 ("owner/admin can rotate invite token"), which nothing in the
     * frozen contract contradicts.
     *
     * Narrower than [canInviteMembers] on purpose. Rotation DESTROYS a link that still works, no
     * operation can undo it, and reading `invite_permission` as authority to rotate would let any
     * member of a public group repeatedly invalidate the link that community printed on a poster.
     */
    val canRotateInviteLink: Boolean,
    /** Name, description, avatar and public username. Owner and Admin. */
    val canEditGroup: Boolean,
    /**
     * Owner only. DECISIONS §7 read literally: "Group chat is normally open to all, but Owner can
     * enable administrator-only posting." Admins run the membership; they do not decide who speaks.
     */
    val canEditSettings: Boolean,
    /** Owner only, permanent, and the only way out for a sole owner. */
    val canDeleteGroup: Boolean,
    /** Owner only. The current owner becomes an Admin, which is what then lets them leave. */
    val canTransferOwnership: Boolean,
    /**
     * False for the Owner: DECISIONS §7 says "Owner must transfer ownership before leaving", and
     * the server answers `409 ownership_transfer_required` regardless of how many members remain.
     */
    val canLeave: Boolean,
) {
    internal companion object {
        fun of(role: GroupRole, settings: GroupSettings): GroupPermissions {
            val elevated = role == GroupRole.OWNER || role == GroupRole.ADMIN
            return GroupPermissions(
                canSendMessages = settings.chatPosting == GroupPostingPolicy.ALL_MEMBERS || elevated,
                canPostToDiscover = settings.discoverPosting == GroupPostingPolicy.ALL_MEMBERS || elevated,
                canInviteMembers = settings.invitePermission == GroupPostingPolicy.ALL_MEMBERS || elevated,
                canRemoveMembers = elevated,
                canChangeRoles = role == GroupRole.OWNER,
                canModerate = elevated,
                canRotateInviteLink = elevated,
                canEditGroup = elevated,
                canEditSettings = role == GroupRole.OWNER,
                canDeleteGroup = role == GroupRole.OWNER,
                canTransferOwnership = role == GroupRole.OWNER,
                canLeave = role != GroupRole.OWNER,
            )
        }
    }
}

// ----------------------------------------------------------------------------------- group

/**
 * A group, as this viewer sees it.
 *
 * SDS §14.1 is what the card needs -- [avatarUrl], [name], [memberCount], [description] -- and SPS
 * §12.1 is why [conversationId] sits beside [id]: the group is the community, the conversation is
 * the chat inside it, and the Chat / Discover / Members sections address different resources.
 *
 * [role] is the CALLER's role, not a property of the group. Two people reading the same group get
 * two different values here, which is also why [permissions] is computed from it.
 */
data class Group(
    val id: GroupId,
    /** The chat underneath the group. Pass this, never [id], to the conversation endpoints. */
    val conversationId: ChatId,
    /** The underlying conversation's title; a rename shows in the chat list and here at once. */
    val name: String,
    val description: String?,
    val avatarUrl: String?,
    val visibility: GroupVisibility,
    /**
     * Non-null only for a public group that sent a complete address. This, not [visibility], is
     * what a share or copy-link action should test.
     */
    val publicAddress: PublicAddress?,
    val role: GroupRole,
    /** False for a public preview. [role] remains the least-privileged fallback for old servers. */
    val isMember: Boolean = true,
    /** DECISIONS §7 caps a group at 500; a 501st join is `group_full`. */
    val memberCount: Int,
    val settings: GroupSettings,
    val createdAt: Instant?,
) {
    /** SPS §12.7's table for this viewer. Derived, so it cannot drift from [role]. */
    val permissions: GroupPermissions get() = GroupPermissions.of(role, settings)

    /**
     * Whether this viewer may remove [member].
     *
     * STRICTLY more senior, which is the server's own rule: an Owner may remove an Admin or a
     * Member, an Admin may remove a Member but not another Admin, and nobody may remove the Owner.
     * A Member may remove nobody.
     *
     * Removing YOURSELF is not this question -- it is leaving, and the server routes it to the same
     * write, so an owner asking to remove themselves gets `ownership_transfer_required` rather than
     * a permission answer. Hence the self case is excluded here and lives on
     * [GroupPermissions.canLeave].
     */
    fun mayRemove(member: GroupMember, me: UserId): Boolean =
        member.user.id != me && rankOf(role) > rankOf(member.role)

    /**
     * Whether this viewer may ban [member].
     *
     * THE SAME QUESTION AS [mayRemove], delegated rather than restated, because on the server it is
     * literally the same permission: `banGroupMember` and `removeGroupMember?action=remove_and_ban`
     * share one action, and DECISIONS §7 pairs the two acts ("Removal can be simple removal or
     * removal plus ban"). A second copy of the seniority comparison is how a screen and a server
     * start disagreeing about who may ban whom, and the disagreement is only visible as a 403 the
     * user cannot act on.
     *
     * A ban REMOVES as well as recording the ban -- that is the server's decision, taken because a
     * ban that left its subject inside the group would leave them reading and posting. The
     * consequence for this type is that a banned member disappears from [GroupMember] lists and
     * [memberCount] drops, so nothing here needs a "banned member" state.
     */
    fun mayBan(member: GroupMember, me: UserId): Boolean = mayRemove(member, me)

    /**
     * Whether this viewer may promote or demote [member].
     *
     * Owner only ([GroupPermissions.canChangeRoles]), never the owner as a target, and never
     * yourself. The last two overlap -- a group has exactly one owner, so an owner targeting
     * themselves and an owner as a target are the same request -- and both are written out because
     * the server answers them differently: `409 ownership_transfer_required` for the owner, and
     * `transferGroupOwnership` is the only operation that moves the role.
     *
     * `owner` is not expressible in the request at all ([GroupRoleAssignment] has two members), so
     * this predicate can never be the thing standing between an admin and a takeover.
     */
    fun mayChangeRole(member: GroupMember, me: UserId): Boolean =
        permissions.canChangeRoles &&
            member.user.id != me &&
            member.role != GroupRole.OWNER

    /**
     * The role [member] would be given if the viewer tapped the single role control on their card.
     *
     * One control with two directions rather than two controls: `updateGroupMemberRole` is one
     * operation whose body carries the target role, an Admin's only available change is to Member
     * and a Member's only available change is to Admin, so a screen offering both would always have
     * one of them disabled or absent. Null when the member's role is one this operation cannot
     * assign -- today only [GroupRole.OWNER], which [mayChangeRole] already refuses.
     */
    fun nextRoleFor(member: GroupMember): GroupRoleAssignment? = when (member.role) {
        GroupRole.MEMBER -> GroupRoleAssignment.ADMIN
        GroupRole.ADMIN -> GroupRoleAssignment.MEMBER
        GroupRole.OWNER -> null
    }

    /** Private groups only: a public group joins immediately and its queue is always empty. */
    val hasJoinQueue: Boolean
        get() = visibility == GroupVisibility.PRIVATE && permissions.canModerate
}

/**
 * One row of the member list (SPS §12.6).
 *
 * [user] is the shared [User], so a name and an avatar render here exactly as they do in a chat
 * header. [role] is what puts an "Admin" badge on the row.
 *
 * [joinedAt] is nullable only as a parse fallback. A row whose date cannot be read still has to be
 * shown: this list is the only place a member can be removed from.
 */
data class GroupMember(
    val user: User,
    val role: GroupRole,
    val joinedAt: Instant?,
)

/**
 * The required `action` query parameter of `removeGroupMember`.
 *
 * Not a wire type -- it never appears in JSON, which is why `protocol/dto/Groups.kt` deliberately
 * declines to model it as `@Serializable`. It is spelled out here because the contract makes the
 * parameter REQUIRED and omitting it is `400 validation_failed` on `action`.
 *
 * [REMOVE_AND_BAN] is STILL UNUSED even now that the ban surface exists, and that is a decision
 * worth stating rather than an oversight. The contract offers two ways to ban -- this query value
 * and `banGroupMember` (`PUT /groups/{id}/bans/{user_id}`) -- and the client uses the second
 * everywhere, for three reasons: it is the operation the ban LIST is the read side of, so one
 * screen is not undoing what another screen did through a different door; the server's
 * `banGroupMember` also rejects the target's pending join request in the same transaction, which
 * the removal path does not; and a single writer means a single place for the 403 and the
 * seniority rule. Keeping the value here is honesty about the parameter, not an invitation.
 */
enum class GroupMemberRemoval { REMOVE, REMOVE_AND_BAN }

// ------------------------------------------------------------- invite links, roles, moderation

/**
 * The two roles `updateGroupMemberRole` can assign.
 *
 * OWNER IS NOT HERE, and its absence is a guarantee rather than a narrowing. The contract's body
 * schema is `enum: [admin, member]`, so promoting somebody to owner is not refused, it is
 * unrepresentable -- `transferGroupOwnership` is the only path to that role, and it is owner-only.
 * If `owner` were ever added to this enum every permission check in the app would still pass and a
 * takeover would open silently, which is why the wire type keeps it out too.
 */
enum class GroupRoleAssignment { ADMIN, MEMBER }

/** The `decision` of `resolveGroupJoinRequest`. Both answer 204; neither is undoable in v1.0.0. */
enum class GroupJoinDecision { APPROVE, REJECT }

/**
 * A group's ONE active invite link. `createOrRotateInviteLink`'s 200 body.
 *
 * [token] IS A CREDENTIAL. Anyone holding it can join a public group or ask a private one; it is
 * not authority inside the group and never opens the history (a new member's first visible message
 * is the one after they joined), but it is the whole of the access control on a private group's
 * front door. Keep it out of logs, out of analytics and out of any error message.
 *
 * THERE IS NO WAY TO READ THIS WITHOUT WRITING IT, and every screen that shows a link is shaped by
 * that. `createOrRotateInviteLink` is the only operation that returns an [GroupInviteLink] and it
 * MUTATES: there is no `GET /groups/{id}/invite-link` anywhere in v1.0.0. For an Owner or an Admin
 * the call therefore rotates -- which invalidates whatever the group had already shared -- so a
 * screen must never make it on its own initiative. See `GroupInviteScreen`.
 *
 * [createdAt] is nullable only as a parse fallback, following [GroupMember.joinedAt]: a link whose
 * date cannot be read still has to be shown, because it is the only copy of the token.
 *
 * There is NO expiry on the wire. The server stores one and nothing sets it in v1, so no screen
 * may claim a link expires -- `InviteLink` has three properties and none of them is `expires_at`.
 */
data class GroupInviteLink(
    /** The full universal link, e.g. `https://swetgram.com/join/<token>`. Server-built; never assembled here. */
    val url: String,
    /** The credential inside [url]. Kept separately because `/join/{invite_token}` takes it alone. */
    val token: String,
    val createdAt: Instant?,
)

/**
 * What the button on an invite preview will do, as the SERVER computed it for this viewer.
 *
 * A closed set of four. `GroupInvitePreview.action` already accounts for who is looking --
 * [ALREADY_MEMBER] and [REQUEST_PENDING] exist so a second tap on an old link neither joins twice
 * nor files a duplicate request -- so a screen renders this rather than re-deriving it from
 * visibility and a membership it cannot see.
 *
 * IT CANNOT SAY "BANNED", and a screen must not pretend otherwise. The enum has no such value, and
 * DECISIONS §7 explains why the server does not invent one: a preview that refused a banned viewer
 * would tell anybody holding the link whether the group had banned them. The ban surfaces on the
 * POST instead, as `403 group_banned`.
 */
enum class GroupInviteAction { JOIN, REQUEST_ACCESS, ALREADY_MEMBER, REQUEST_PENDING }

/**
 * A group as somebody outside it sees it, resolved from an invite token.
 *
 * Deliberately NOT a [Group], and it must never be widened into one. There is no `conversation_id`
 * here and no member list: the viewer is not a member, and a conversation id is the realtime
 * channel name, so handing it out before a private group's admins have approved anybody would leak
 * the address of a chat they have not been let into. `role`, `settings` and `permissions` are
 * likewise absent because they all describe a membership this viewer does not have.
 *
 * [action] is null when this build could not read what the server said to offer. That is not a
 * fifth kind of action -- it is the absence of one, and the screen shows the group with no button,
 * which is the fail-closed direction this file uses everywhere else.
 */
data class GroupInvite(
    val groupId: GroupId,
    val name: String,
    val description: String?,
    val avatarUrl: String?,
    val visibility: GroupVisibility,
    val memberCount: Int,
    /** Null means "no button": an action this build cannot name is not one it may perform. */
    val action: GroupInviteAction?,
)

/**
 * What `actOnGroupInvite` actually did.
 *
 * A sealed result rather than a status enum, because the two outcomes carry different things and a
 * screen must not be able to read the wrong one: a join hands back the [Group] and lands the user
 * inside it, while a request hands back nothing but the group's id and lands them on "an admin has
 * to approve this".
 *
 * BRANCHED ON THE SERVER'S `status`, NOT ON THE HTTP CODE and not on whether a group came back.
 * The contract sends 200 for a join and 202 for a request, and `GroupJoinResult.group` is optional
 * on both; the DTO's own note is that `status` is the property to branch on. [Joined.group] is
 * therefore nullable -- a join whose body omitted the group is still a join, and the screen then
 * navigates by [Joined.groupId] and lets the group screen load it.
 */
sealed interface GroupJoinOutcome {
    /** `200 joined`. Also the answer to joining a group you are already in -- joining is idempotent. */
    data class Joined(val groupId: GroupId, val group: Group?) : GroupJoinOutcome

    /** `202 pending_approval`. A private group's admins now have a request; nothing else happened. */
    data class Requested(val groupId: GroupId) : GroupJoinOutcome
}

/**
 * One row of a private group's join queue.
 *
 * [isPending] rather than a three-valued status enum, and this is the same narrowing every other
 * wire enum in this file gets. `listGroupJoinRequests` returns pending requests -- the server pages
 * off an index that is partial on `status = 'pending'` -- so `approved` and `rejected` should never
 * arrive; if one does, or a status this build cannot read does, the honest answer is that it is not
 * something to offer Approve and Reject on. Publishing all three states would put a vocabulary in
 * the domain that no screen has a use for and that the server does not send.
 *
 * [createdAt] is nullable as a parse fallback, like every other date here: this queue is the only
 * place a request can be resolved from, so a row survives a date the client cannot read.
 */
data class GroupJoinRequestEntry(
    val id: GroupJoinRequestId,
    val groupId: GroupId,
    /** The applicant. Not a member, so their presence is projected as `shares_group = false`. */
    val user: User,
    /** False for anything that is not `pending`, including a status this build could not read. */
    val isPending: Boolean,
    val createdAt: Instant?,
)

/**
 * One row of a group's ban list.
 *
 * [bannedBy] is a full [User] because the contract sends one, and because "who did this" is the
 * question a second admin asks before undoing it. That person may no longer be in the group -- the
 * server keeps the reference alive precisely so the answer survives their departure -- which is why
 * their presence is asked for rather than assumed.
 *
 * There is no reason and no expiry. `GroupBan` has three properties: the user, who banned them and
 * when. A screen must not invent a "banned for" line.
 */
data class GroupBanEntry(
    val user: User,
    val bannedBy: User,
    val createdAt: Instant?,
)

// -------------------------------------------------------------------------------- creation

/**
 * The one choice at creation that can never be taken back, shaped so the illegal halves cannot be
 * written down.
 *
 * The contract states it as an `if`/`then` pair -- a public group REQUIRES a username, a private
 * group must not have one -- which kotlinx.serialization cannot enforce and the server therefore
 * validates: both `{"visibility":"private","username":"x"}` and a public group with no username are
 * `400 validation_failed`, confirmed against the deployed server. A sealed choice makes both of
 * those uncompilable instead of a round trip the user cannot act on.
 *
 * Visibility is immutable once the group exists. `UpdateGroupRequest` offers no way to change it,
 * because turning a private group public would retroactively expose its member list and its
 * Discover area, and turning a public one private would strand its username and every QR code
 * printed from it.
 */
sealed interface GroupVisibilityChoice {
    /** Discoverable, reachable at `/g/{username}`, and joinable straight from a preview. */
    data class Public(val username: String) : GroupVisibilityChoice

    /** Invite-link only, with administrator-approved join requests. */
    data object Private : GroupVisibilityChoice
}

/**
 * A group about to be created. SPS §12.2: name required, avatar and description optional.
 *
 * [initialMemberIds] is the ONLY way anyone but the creator joins in this pass, and it is worth
 * being blunt about why: v1.0.0 has no add-member operation at all. `maxItems: 100` and
 * `uniqueItems: true` on the wire; the repository de-duplicates before sending, since a `List`
 * cannot express uniqueness and duplicates would collide on the server's membership primary key.
 *
 * There is no `client_id` and no idempotency key, because `CreateGroupRequest` has neither. A
 * repeated create makes a second group, so this request is never retried -- see the repository.
 */
data class NewGroup(
    val name: String,
    val visibility: GroupVisibilityChoice,
    val description: String? = null,
    /** A media id from a completed upload, not a URL. */
    val avatarMediaId: String? = null,
    val initialMemberIds: List<UserId> = emptyList(),
)

// ------------------------------------------------------------------------------------ edit

/**
 * A pending change to a group's identity. Everything defaults to "leave alone".
 *
 * [description] and [avatarMediaId] take [FieldEdit] because the contract distinguishes an absent
 * key ("leave alone") from an explicit JSON null ("remove this"), and a plain nullable cannot carry
 * both -- `ProtocolJson` has `explicitNulls = false`, so a null would always mean "leave alone" and
 * a description could never be emptied.
 *
 * [username] is a plain `String?` and has no [FieldEdit.Clear], which is a narrowing of the
 * contract and is stated rather than hidden. The contract types it `oneOf [Username, null]`, but a
 * null is not usable on either kind of group the client can reach: on a public group the deployed
 * server answers `400 validation_failed` -- "A public group must keep a username" -- and a private
 * group has no username to clear. Offering a control whose every use is a 400 would be worse than
 * not having it.
 *
 * There is no `version` here. Only the settings carry one; a rename is not under optimistic
 * concurrency and must not consume the settings counter.
 */
data class GroupEdit(
    val name: String? = null,
    val description: FieldEdit<String> = FieldEdit.Unchanged,
    /** Public groups only, and subject to the same 30-day rules as a user's username. */
    val username: String? = null,
    val avatarMediaId: FieldEdit<String> = FieldEdit.Unchanged,
) {
    /**
     * The contract's `minProperties: 1`. An empty body is `400 validation_failed` on `body`,
     * confirmed against the deployed server, so it is refused before it costs a round trip.
     */
    val hasChanges: Boolean
        get() = name != null ||
            username != null ||
            description != FieldEdit.Unchanged ||
            avatarMediaId != FieldEdit.Unchanged
}

// -------------------------------------------------------------------------------- failures

/**
 * The named ways a group operation can fail, as something a screen can branch on.
 *
 * This exists because of IMPLEMENTATION_NOTES §10: the error codes live in
 * `protocol/dto/Errors.kt`, and a ViewModel comparing `ApiError.Conflict.code` against
 * `ErrorCodes.OWNERSHIP_TRANSFER_REQUIRED` would have to import `protocol` to do it. Mapping the
 * codes once, at the boundary, keeps the wire vocabulary below the UI -- and turns a set of loose
 * strings into a `when` the compiler checks.
 *
 * Use [com.swetdot.swetgram.data.groups.asGroupFailure]. [OTHER] is for everything with no group
 * meaning -- offline, timeouts, 5xx, an expired session -- which keeps their existing handling
 * rather than flattening them into this enum.
 */
enum class GroupFailure {
    /**
     * The Owner tried to leave, or to remove themselves. The remedy is transfer or delete, and a
     * screen must offer one of them: nothing else clears this.
     */
    OWNERSHIP_TRANSFER_REQUIRED,

    /** The caller's role does not allow the action. Usually means the UI offered it in error. */
    INSUFFICIENT_ROLE,

    /**
     * The group is at DECISIONS §7's 500-member cap.
     *
     * Reachable from two different directions and both need saying, because the second is not
     * obvious: a public join into a full group, and an ADMIN approving a join request for a group
     * that filled up while the request sat in the queue. The cap is deliberately not enforced when
     * a private group's request is FILED -- refusing then would publish the exact member count to
     * somebody who is not in the group -- so the queue can hold requests it cannot approve.
     */
    GROUP_FULL,

    /**
     * The user is banned from this group.
     *
     * Only ever answered by `actOnGroupInvite`. The preview cannot report it -- its `action` enum
     * has no such value on purpose -- so a banned user sees a normal-looking Join button and learns
     * the answer when they press it. That is the intended shape, not a defect to smooth over.
     */
    BANNED,

    /**
     * A private group already holds a pending request from this user.
     *
     * `409 join_request_pending`, and this API has exactly one producer of it. Not a failure in any
     * sense the user cares about -- they have already asked, and asking again changes nothing -- so
     * a screen should report it as the state it describes rather than as an error.
     */
    JOIN_REQUEST_PENDING,

    /**
     * The target of a moderation action is not a live member of the group.
     *
     * `403 not_conversation_member`, answered by `banGroupMember` for somebody who has already left
     * or already been removed. Distinct from [UNAVAILABLE], which is about the GROUP: here the
     * group is visible and the person is not in it.
     */
    NOT_A_MEMBER,

    /** The public username is taken -- point the form at the username field. */
    USERNAME_TAKEN,

    /** A username was changed within the last 30 days. Not retryable today. */
    USERNAME_COOLDOWN,

    /** `createGroup` requires a verified email. Send the user to the verification screen. */
    EMAIL_NOT_VERIFIED,

    /**
     * No such group, or the caller may not see it. The two are deliberately ONE answer: a distinct
     * "not a member" would turn any group id into a live check on whether that private group
     * exists, and group ids are not secret.
     */
    UNAVAILABLE,

    /** DECISIONS §12 caps group creation at 5 a day. */
    RATE_LIMITED,

    /** The request was malformed -- field errors are on the underlying `ApiError.Validation`. */
    INVALID,

    /** Nothing group-specific. Handle it as whatever `ApiError` it already was. */
    OTHER,
}
